import { IDisposable } from "../../Global/Base/IDisposable";
import { NumberUtils } from "../../Global/Base/Utils/NumberUtils";
import { EventHelper } from "./Helpers/EventHelper";
/**
 * Pokud je cancelbyclick nastaveno na true, tak se popup zavre pri kliknuti mimo jeho obsah, toto je realizovano tak, ze na body je event click, ktery zavira popup,
 * pokud je klikano do popupu, tak ten si odchytava click a dale ho nepropaguje
 * Toto je potreba udelat i na prvku, ktery popup otvira, cili na udalosti onclick prvku, ktery otevira popup se musi zavolat stopropagation !!!
 */
export class PopupMenu implements IDisposable {
    //#region Fields
    public element: HTMLElement = document.createElement("div");
    public visible: boolean = false;

    private _bodyClickListener: EventListener;
    private _popupClickListener: EventListener;

    //#endregion Fields

    //#region Constructors
    /**
     * Je nastaveno v Construktoru
     *
     * Kdyz je cancelbyclick nastaveno na true, tak se popup zavre pri kliknuti mimo jeho obsah, toto je realizovano tak, ze na body je event click, ktery zavira popup,
     * pokud je klikano do popupu, tak ten si odchytava click a dale ho nepropaguje
     * Toto je potreba udelat i na prvku, ktery popup otvira, cili na udalosti onclick prvku, ktery otevira popup nastavit callingEvent.stopPropagation()!!!
     *
     * @param innerElement element, ktery se bude v popup menu zobrazovat
     * @param cancelByClick pokud je true, tak se okno automaticky zavre, pokud se klikne mimo okno, defaultne true
     * @param width sirka komponenty jako string (aby se daly zadat i %)
     * @param height vyska komponenty jako string (aby se daly zadat i %)
     * @param top odsazeni od vrchniho okraje stranky jako string (aby se daly zadat i %)
     * @param left odsazeni od leveho okraje stranky jako string (aby se daly zadat i %)
     * @param bottom odsazeni od dolniho okraje stranky
     * @param right odsazeni od praveho okraje stranky
     */
    constructor(innerElement?: HTMLElement, private cancelByClick: boolean = true, width: string = "50%", height: string = "50%", top: string = "25%", left: string = "25%", expandToContent: boolean = true) {
        this._initialize(innerElement, expandToContent);

        this._setSize(width, height);
        this.setIndent(top, left);
        this._setListeners();
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Inicializace komponenty
     * @param innerElement element, ktery se bude v popup menu zobrazovat
     */
    private _initialize(innerElement?: HTMLElement, expandToContent: boolean = true): void {
        if (innerElement) {
            innerElement.style.width = "100%";
            innerElement.style.height = "100%";
            this.element.appendChild(innerElement);
        }
        this.element.classList.add("PopupMenu");
        if (!expandToContent)
            this.element.classList.add("sizeLimited");

        document.body.appendChild(this.element);
    }

    /**
     * Nastaveni listeneru
     */
    private _setListeners(): void {
        if (this.cancelByClick) {
            // všechny popupy zavíráme pokud došlo ke kliknutí někam do okna (i pokud došlo k zastavení této události - událost "clickCancelled")
            window.addEventListener("click", this._bodyClickListener = (_e: MouseEvent) => {
                if (this.visible)
                    this.setVisibility(false);
            });

            window.addEventListener("clickCancelled", this._bodyClickListener);

            this.element.addEventListener("click", this._popupClickListener = (e: MouseEvent) => {
                //#25560 řeší bug firefoxu https://bugzilla.mozilla.org/show_bug.cgi?id=136407
                //click odchytil popup, proto víme, že select, který ho vyvoval byl umístěn na něm a v takovém případě nebude volat clickCancelled, který by se odchytil na window a zavřel popup
                if (!e.clientX && !e.clientY && e.target && e.target instanceof HTMLSelectElement) {
                    e.stopPropagation();
                    return;
                }
                EventHelper.stopPropagation(e);
            });
        }
    }

    /**
     * Nastaveni odsazeni
     * @param top odsazeni od vrchinoho okraje stranky
     * @param left odsazeni od leveho okraje stranky
     * @param bottom odsazeni od dolniho okraje stranky
     * @param right odsazeni od praveho okraje stranky
     */
    public setIndent(top: string = null, left: string = null, bottom: string = null, right: string = null): void {
        if (top)
            this.element.style.top = top;

        if (left)
            this.element.style.left = left;

        if (bottom)
            this.element.style.bottom = bottom;

        if (right)
            this.element.style.right = right;
    }

    /**
     * Nastaveni rozmeru
     * @param width sirka popup menu
     * @param height vyska popup menu
     */
    private _setSize(width: string | number, height: string | number): void {
        this.element.style.width = typeof width === "string" ? width : NumberUtils.transformLengthToCss(width ? width : null);
        this.element.style.height = typeof height === "string" ? height : NumberUtils.transformLengthToCss(height ? height : null);
    }

    /**
     * Nastaví minimální rozměry pro popup (bere v potaz už nastavené maximální rozměry a nepřekročí je)
     * @param width maximalni šířka popup menu
     * @param height maximalni výška popup menu
     */
    private _setMinSize(width: string | number, height: string | number): void {
        if (typeof width === "string") {
            this.element.style.minWidth = width;
        } else {
            // vypočítané max šířka
            const maxWidth = parseInt(window.getComputedStyle(this.element, null).getPropertyValue("max-width"));

            const finalWidth = maxWidth && maxWidth < width ? maxWidth : width;

            this.element.style.minWidth = NumberUtils.transformLengthToCss(finalWidth ? finalWidth : null);
        }

        if (typeof height === "string") {
            this.element.style.minHeight = height;
        } else {
            const maxHeight = parseInt(window.getComputedStyle(this.element, null).getPropertyValue("max-height"));

            const finalHeight = maxHeight && maxHeight < height ? maxHeight : height;

            this.element.style.minHeight = NumberUtils.transformLengthToCss(finalHeight ? finalHeight : null);
        }
    }

    /**
     * Nastaveni viditelnosti komponenty
     * @param visible boolean
     */
    public setVisibility(visible: boolean): void {
        this.visible = visible;
        this.element.style.display = visible ? "" : "none";
    }

    /**
     * Kontrola, zda se menu vleze pod zadane souradnice
     */
    public checkIfFitsVerticaly(): boolean {
        const windowH = window.innerHeight;
        const offsetTop = this.element.getBoundingClientRect().top;
        if (windowH - (this.element.clientHeight + offsetTop) > 0)
            return true;
        else
            return false;
    }

    public arangePopupToEditableControl(popUpElemenet: HTMLElement, targetControlElement: HTMLElement, hasFocus: boolean, gridRect: ClientRect = null, width: number = null, height: number = null): void {
        if (popUpElemenet != undefined) {
            const rect = targetControlElement.getBoundingClientRect();

            if (gridRect && hasFocus) { //jsem na datagridu a ma focus
                const upper = rect.bottom - gridRect.top < -1;
                const downer = gridRect.bottom - rect.top < -1;

                if (this.visible) { //je viditelny a komponenta leze mimo viditelnou cast datagridu
                    if (upper || downer) { //nahoru nebo dolu
                        this.setVisibility(false);
                        return; //pokud ho skryju tak koncim vykonani funcke - pomerne dulezita vec!!!
                    }
                } else { //je neviditelny a komponenta leze do viditelne casti datagridu a ma focus
                    if (!upper && !downer)
                        this.setVisibility(true);
                }
            }

            // pokud je natvrdo zadaná výška nebo šířka, tak se nastaví i elementu
            if (width || height) {
                this._setSize(width, height);
            } else {
                // pokud rozměry vypočítáváme z z cílového elementu,
                // tak se podle něj nastaví pouze minimální rozměry a zbytek se přizpůsobí podle obsahu až do případného maximálního rozměru
                width = targetControlElement.offsetWidth;
                height = targetControlElement.offsetHeight;
                this._setMinSize(width, height);
            }

            let errorTop: string, errorLeft: string;
            if (window.innerHeight - rect.bottom < height) { //vyska okna - spodni okraj editovatelneho pole je mensi nez velikost validacni hlasky
                errorTop = rect.top - height + "px";
            } else {
                errorTop = rect.bottom + "px";
            }

            if (window.innerWidth - rect.left < width) { //sirka okna
                errorLeft = rect.right - width + "px";
            } else {
                errorLeft = rect.left + "px";
            }

            this.setIndent(errorTop, errorLeft);
        }
    }

    /**
     * Zavreni PopupMenu
     */
    public close(): void {
        this.setVisibility(false);
        this.dispose();
    }

    /**
     * Destruktor
     */
    public dispose(): void {
        this.element.parentElement.removeChild(this.element);

        if (this.cancelByClick)
            document.body.removeEventListener("click", this._bodyClickListener);

        this.element.removeEventListener("click", this._popupClickListener);
    }

    //#endregion Methods
}
